import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Mail, Power, PowerOff, Edit, Trash2, Building2, Briefcase, Loader2, Users as UsersIcon } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { listUsers, deleteUser, toggleUserStatus, resendInvite, type User } from "@/services/usersService";
import { UserForm } from "./UserForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface UserManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const UserManagement = ({ onBack }: UserManagementProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersResponse = await listUsers({ limit: 100 });

      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data.users);
      } else {
        throw new Error(usersResponse.error?.message || "Erro ao carregar usuários");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os usuários.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async () => {
    await loadUsers();
    setViewMode("list");
    setEditingUser(null);
  };

  const handleNewUser = () => {
    setEditingUser(null);
    setViewMode("form");
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setViewMode("form");
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      // Buscar o usuário atual para determinar o novo status
      const user = users.find(u => u.id === userId);
      if (!user) {
        throw new Error("Usuário não encontrado");
      }

      // Determinar o novo status baseado no status atual
      // Se está ativo, vai para inativo; caso contrário, vai para ativo
      let newStatusId: number;
      const currentStatusName = (user.status_name || '').toLowerCase();

      if (currentStatusName === 'active') {
        // Se está ativo, desativar - buscar status "inactive" (geralmente ID 14)
        // Mapeamento comum: active=13, inactive=14, pending=15, blocked=16
        newStatusId = 14; // inactive
      } else {
        // Se não está ativo (pending, inactive, blocked), ativar
        // Sempre ativar para status "active" (geralmente ID 13)
        newStatusId = 13; // active
      }

      // Validação adicional
      if (!newStatusId || isNaN(newStatusId)) {
        throw new Error("Não foi possível determinar o novo status do usuário");
      }

      console.log('Toggle status:', { userId, currentStatus: user.status_name, newStatusId });

      const response = await toggleUserStatus(userId, newStatusId);
      if (response.success) {
        toast({
          title: "Status alterado",
          description: "O status do usuário foi alterado com sucesso.",
        });
        await loadUsers();
      } else {
        throw new Error(response.error?.message || "Erro ao alterar status");
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao alterar status do usuário.",
        variant: "destructive"
      });
    }
  };

  const handleResendInvite = async (userId: string) => {
    try {
      const response = await resendInvite(userId);
      if (response.success) {
        toast({
          title: "E-mail reenviado",
          description: "Um novo e-mail de boas-vindas foi enviado. O link anterior foi invalidado.",
        });
      } else {
        throw new Error(response.error?.message || "Erro ao reenviar convite");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao reenviar convite.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      const response = await deleteUser(userToDelete.id);
      if (response.success) {
        setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
        setUserToDelete(null);
        toast({
          title: "Usuário excluído",
          description: `${userToDelete.name} perdeu o acesso à plataforma. O histórico de ações foi mantido na auditoria.`,
        });
      } else {
        throw new Error(response.error?.message || "Erro ao excluir usuário");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao excluir usuário.",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (statusName: string) => {
    switch (statusName) {
      case 'active':
        return <Badge variant="default">Ativo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inativo</Badge>;
      case 'pending':
        return <Badge variant="outline">Pendente</Badge>;
      default:
        return <Badge>{statusName}</Badge>;
    }
  };

  if (viewMode === "form") {
    return (
      <UserForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveUser}
        editingUser={editingUser}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_users.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar usuários.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={UsersIcon}
        title="Gerenciamento de Usuários"
        description="Controle de acesso e permissões dos usuários"
        onBack={onBack}
        actions={
          canCreate("users") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewUser}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
          )
        }
      />

      <main className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>Usuários Cadastrados</CardTitle>
            <CardDescription>
              Lista de todos os usuários com seus respectivos acessos e status
            </CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <UsersIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Nenhum usuário cadastrado</p>
                  <Button onClick={handleNewUser} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Usuário
                  </Button>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Clientes</TableHead>
                    <TableHead>Workspaces</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.profile_name ? (
                          <Badge variant="secondary">{user.profile_name}</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {user.clients && user.clients.length > 0 ? (
                            <>
                              {user.clients.slice(0, 2).map((client) => (
                                <div key={client.id} className="flex items-center space-x-2">
                                  <Building2 className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-sm">{client.company}</span>
                                </div>
                              ))}
                              {user.clients.length > 2 && (
                                <div className="text-xs text-muted-foreground">
                                  +{user.clients.length - 2} mais
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.workspaces && user.workspaces.length > 0 ? (
                            <>
                              {user.workspaces.slice(0, 2).map(workspace => (
                                <Badge key={workspace.id} variant="secondary" className="text-xs">
                                  <Briefcase className="h-3 w-3 mr-1" />
                                  {workspace.name}
                                </Badge>
                              ))}
                              {user.workspaces.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{user.workspaces.length - 2}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(user.status_name || 'inactive')}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {currentUser && user.id !== currentUser.id && canUpdate("users") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleEdit(user)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Editar usuário</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {user.access_type_name === 'password' && currentUser && user.id !== currentUser.id && canUpdate("users") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleResendInvite(user.id)}
                                  className="hover:bg-blue-50"
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Reenviar e-mail de boas-vindas</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {currentUser && user.id !== currentUser.id && canUpdate("users") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleToggleStatus(user.id)}
                                  className={user.status_name === 'active' ? "hover:bg-destructive/10 text-destructive" : "hover:bg-green-50 text-green-700"}
                                >
                                  {user.status_name === 'active' ? (
                                    <PowerOff className="h-4 w-4" />
                                  ) : (
                                    <Power className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{user.status_name === 'active' ? 'Desativar usuário' : 'Ativar usuário'}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {currentUser && user.id !== currentUser.id && canDelete("users") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setUserToDelete(user)}
                                  className="hover:bg-destructive/10 text-destructive"
                                  aria-label={`Excluir ${user.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Excluir usuário</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>

      <AlertDialog
        open={!!userToDelete}
        onOpenChange={(open) => {
          if (!open && !deleting) setUserToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Ao excluir{" "}
                  <strong className="text-foreground">{userToDelete?.name}</strong>{" "}
                  ({userToDelete?.email}):
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>O acesso à plataforma é revogado imediatamente, inclusive sessões abertas</li>
                  <li>O usuário deixa de aparecer na lista de usuários cadastrados</li>
                  <li>Convites e links de recuperação de senha pendentes são invalidados</li>
                </ul>
                <p>
                  O registro e o histórico de ações do usuário são mantidos e
                  continuam disponíveis na auditoria.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir usuário"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionGuard>
  );
};

